// Types
import ComparisonItem from '@/types/comparison-item';
import Dictionary from '@/types/dictionary';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Locale from '@/types/locale';
import LocalizedAllocationDetails from '@/types/localized-allocation-details';
import Nullable from '@/types/nullable';
import Questionnaire from '@/types/questionnaire';
import RiskProfileItem, { riskProfileUpRisk } from '@/types/risk-profile-item';
import User from '@/types/user';
import {
  RiskProfileRule,
  RiskProfileRuleType,
  RiskProfileAssetAllocationRule,
  RiskProfileRiskLimitRule,
  RiskProfileFundTypeRule,
  InvestmentRiskLevel,
  RiskAssetType,
  InvestmentAssetType,
  RiskProfileRuleTarget,
  RiskProfileRuleData,
  RiskProfileAllowedRiskRule,
  RiskLevelOperator,
} from '../types/risk-profiles';
import { StateOmniRiskProfileConfig } from '@/types/risk-profiles/omni/omni-risk-profile-context-types';

// Utils
import { formatPercentage, formatMessageWithLocale, formatLocalizedString, formatNumber, LOCALE } from './i18n';
import { DataPoints } from '../modules/proposal/DAOs/DataPoints';
import { deepClone } from './objects';
import { sum } from './math';
import { QUESTIONNAIRE_TYPES } from './questionnaire';
import { PERMISSIONS, userHasPermission } from './permissions';
import { getDetailedAssetForAssetType } from './comparison/asset-allocations';
import sortBy from 'lodash/sortBy';

export let DEFAULT_RISK_COLORS: { [key in RiskProfileRuleType]: Dictionary<string> } = {
  ASSET_ALLOCATION: {
    [InvestmentAssetType.Equity]: '#326ECE',
    [InvestmentAssetType.FixedIncome]: '#25A79A',
    [InvestmentAssetType.Cash]: '#EB7E1E',
    [InvestmentAssetType.Other]: '#FFBE0A',
    [InvestmentAssetType.AlternativeInvestment]: '#77001e', // $burgundy-3
    [InvestmentAssetType.RealAssets]: '#6BB4B1', // $cyan-3
    RISK_OTHER: '#2c314d',
  },
  RISK_LIMIT: {
    RISK_LOWER_EQ: '#25A79A',
    RISK_GREATER: '#FFBE0A',
    RISK_OTHER: '#2c314d',
  },
  ALLOWED_RISK: {
    RISK_LOWER_EQ: '#25A79A',
    RISK_GREATER: '#FFBE0A',
    RISK_OTHER: '#2c314d',
  },
  FUND_TYPE: {
    RISK_LOWER_EQ: '#25A79A',
    RISK_GREATER: '#FFBE0A',
    RISK_OTHER: '#2c314d',
  },
};

export const RISK_COLORS_THEME_LIGHT: { [key in RiskProfileRuleType]: Dictionary<string> } = {
  ASSET_ALLOCATION: {
    [InvestmentAssetType.Equity]: '#BFD9BF', // $celadon-3
    [InvestmentAssetType.FixedIncome]: '#41889f', // $sea-3
    [InvestmentAssetType.Cash]: '#CE763B', // $copper-3
    [InvestmentAssetType.AlternativeInvestment]: '#77001e', // $burgundy-3
    [InvestmentAssetType.RealAssets]: '#6BB4B1', // $cyan-3
    [InvestmentAssetType.Other]: '#E2AF47', // $golden-3
    RISK_OTHER: '#D0D4DA',
  },
  RISK_LIMIT: {
    RISK_LOWER_EQ: '#41889F', // $sea-3
    RISK_GREATER: '#E2AF47',
    RISK_OTHER: '#D0D4DA',
  },
  ALLOWED_RISK: {
    RISK_LOWER_EQ: '#41889f', // $sea-3
    RISK_GREATER: '#E2AF47',
    RISK_OTHER: '#D0D4DA',
  },
  FUND_TYPE: {
    RISK_LOWER_EQ: '#41889f', // $sea-3
    RISK_GREATER: '#E2AF47',
    RISK_OTHER: '#D0D4DA',
  },
};

export const ACCESSIBLE_RISK_COLORS: { [key in RiskProfileRuleType]: Dictionary<string> } = {
  ASSET_ALLOCATION: {
    [InvestmentAssetType.Equity]: '#22428C',
    [InvestmentAssetType.FixedIncome]: '#23A497',
    [InvestmentAssetType.Cash]: '#6E3812',
    [InvestmentAssetType.Other]: '#D9A200',
    [InvestmentAssetType.AlternativeInvestment]: '#77001E',
    [InvestmentAssetType.RealAssets]: '#4F9A97',
    RISK_OTHER: '#2C314D',
  },
  RISK_LIMIT: {
    RISK_LOWER_EQ: '#41889F', // $sea-3
    RISK_GREATER: '#E2AF47',
    RISK_OTHER: '#D0D4DA',
  },
  ALLOWED_RISK: {
    RISK_LOWER_EQ: '#41889f', // $sea-3
    RISK_GREATER: '#E2AF47',
    RISK_OTHER: '#D0D4DA',
  },
  FUND_TYPE: {
    RISK_LOWER_EQ: '#41889f', // $sea-3
    RISK_GREATER: '#E2AF47',
    RISK_OTHER: '#D0D4DA',
  },
};

export const riskLevels = [
  InvestmentRiskLevel.VeryLow,
  InvestmentRiskLevel.Low,
  InvestmentRiskLevel.LowMedium,
  InvestmentRiskLevel.Medium,
  InvestmentRiskLevel.MediumHigh,
  InvestmentRiskLevel.High,
  InvestmentRiskLevel.VeryHigh,
];

export const calculateRiskProfile = (score?: number, currentQuestionnaire?: KycQuestionnaire) => {
  const profiles = currentQuestionnaire ? currentQuestionnaire.riskProfiles : undefined;

  let profile;
  if (score && profiles) {
    profile = profiles.filter(p => !!p.scoreRange && p.scoreRange.min <= score && score <= p.scoreRange.max)[0];
  }

  return profile;
};

/**
 * Gets the questionnaire that should be used by the user - prioritize enterprise questionnaires over public ones.
 * @param user Current authenticated user
 * @param questionnaires Questionnaires available for the user
 */
export const getActiveQuestionnaire = (questionnaires: KycQuestionnaire[]) =>
  // Questionnaires a now sorted on the backend, so the enterprises ones appear first.
  questionnaires[0];

type RiskProfileWithQuestionnaire = {
  questionnaireId: string;
  profile: KycQuestionnaire['riskProfiles'][0];
};

/**
 * Gets all available risk profiles from all questionnaires with their source questionnaire IDs
 * @param questionnaires Array of KYC questionnaires
 * @returns Array of risk profiles with questionnaire IDs
 */
export const getRiskProfilesWithQuestionnaireContext = (questionnaires: KycQuestionnaire[]): RiskProfileWithQuestionnaire[] => {
  if (!questionnaires?.length) {
    return [];
  }

  return questionnaires.flatMap(questionnaire =>
    (questionnaire.riskProfiles || []).map(profile => ({
      questionnaireId: questionnaire._id,
      profile,
    })),
  );
};

export const splitValuesArray = <T>(valuesArray: T[], value: T, operator: RiskLevelOperator) => {
  const index = valuesArray.findIndex(level => level === value);

  let included = [];
  let excluded = [];

  switch (operator) {
    case RiskLevelOperator.LT:
      included = valuesArray.slice(0, index);
      excluded = valuesArray.slice(index);
      break;
    case RiskLevelOperator.LTE:
      included = valuesArray.slice(0, index + 1);
      excluded = valuesArray.slice(index + 1);
      break;
    case RiskLevelOperator.EQ:
    case RiskLevelOperator.CD:
      included = [value];
      excluded = valuesArray.filter(v => v !== value);
      break;
    case RiskLevelOperator.GT:
      included = valuesArray.slice(index + 1);
      excluded = valuesArray.slice(0, index + 1);
      break;
    case RiskLevelOperator.GTE:
      included = valuesArray.slice(index);
      excluded = valuesArray.slice(0, index);
      break;
  }

  return [included, excluded];
};

export const splitItemRiskValues = <T>(
  valuesArray: T[],
  value: T,
  comparedItem: ComparisonItem,
  operator: RiskLevelOperator,
  accessor: (item: ComparisonItem, value: T) => number,
) => {
  const [included, excluded] = splitValuesArray(valuesArray, value, operator);

  const sumRisk = (acc: number, val: T) => acc + ((comparedItem && accessor(comparedItem, val)) || 0);

  return {
    included: included.reduce(sumRisk, 0),
    excluded: excluded.reduce(sumRisk, 0),
  };
};

const defaultRiskLevelAccessor = (item: ComparisonItem, value: InvestmentRiskLevel): number => (item.riskRating && item.riskRating[value]) || 0;

export const condensedRiskLevelAccessor = (item: ComparisonItem, value: InvestmentRiskLevel): number => {
  const riskRating = item.riskRating || {};

  switch (value) {
    case InvestmentRiskLevel.Low: {
      const l = riskRating[InvestmentRiskLevel.Low] || 0;
      const lm = 0.5 * (riskRating[InvestmentRiskLevel.LowMedium] || 0);

      return l + lm;
    }
    case InvestmentRiskLevel.Medium: {
      const m = riskRating[InvestmentRiskLevel.Medium] || 0;
      const lm = 0.5 * (riskRating[InvestmentRiskLevel.LowMedium] || 0);
      const mh = 0.5 * (riskRating[InvestmentRiskLevel.MediumHigh] || 0);
      return m + lm + mh;
    }
    case InvestmentRiskLevel.High: {
      const h = riskRating[InvestmentRiskLevel.High] || 0;
      const mh = 0.5 * (riskRating[InvestmentRiskLevel.MediumHigh] || 0);

      return h + mh;
    }
    default: {
      return defaultRiskLevelAccessor(item, value);
    }
  }
};

const splitRiskLevelValues = (rule: RiskProfileRiskLimitRule | RiskProfileAllowedRiskRule, comparedItem: ComparisonItem, operator: RiskLevelOperator) => {
  const accessor =
    rule.ruleType !== RiskProfileRuleType.AllowedRisk || operator !== RiskLevelOperator.CD ? defaultRiskLevelAccessor : condensedRiskLevelAccessor;

  return splitItemRiskValues(riskLevels, rule.value.riskLevel, comparedItem, operator, accessor);
};

const splitRiskAssetTypeValues = (rule: RiskProfileFundTypeRule, comparedItem: ComparisonItem) => {
  const fundTypes = [RiskAssetType.MoneyMarket, RiskAssetType.Income, RiskAssetType.IncomeGrowth, RiskAssetType.Growth];

  const accessor = (item: ComparisonItem, value: RiskAssetType) => (item.riskAssetType && item.riskAssetType[value]) || 0;

  return splitItemRiskValues(fundTypes, rule.value.fundType, comparedItem, RiskLevelOperator.LTE, accessor);
};

export const formatRiskProfileTargetMinMax = (min?: number, max?: number, locale?: Locale): string => {
  if (min == null && max == null) {
    return '';
  }

  const getFormattedValue = (value: number): string => {
    const decimalPlaces = !Number.isInteger(value) ? 1 : 0;
    return formatNumber(value, decimalPlaces, locale);
  };

  if (min != null && max == null) {
    return `Min. ${getFormattedValue(min)}%`;
  }

  if (min == null && max != null) {
    return `Max. ${getFormattedValue(max)}%`;
  }

  if (min != null && max != null && min === max) {
    return `${getFormattedValue(max)}%`;
  }

  // At this point, all edge cases have been handled:
  // - Only min defined -> "Min. X%"
  // - Only max defined -> "Max. Y%"
  // - min === max -> "X%"
  // For all other cases, we fall back to a range display.

  const formattedMinRange = getFormattedValue(min!);
  const formattedMaxRange = getFormattedValue(max!);

  return `${formattedMinRange} - ${formattedMaxRange}%`;
};

export const formatMaxTarget = (target: number, precision: number) => {
  if (Math.fround(target) === 0) {
    return formatPercentage(target, precision);
  }

  return `0 - ${formatPercentage(target, precision)}`;
};

export const getTargetValueFromMinMax = (min?: number, max?: number) => {
  if (min != null && max != null) {
    // Get middle between max and min
    return (max + min) / 2;
  }

  if (min != null) {
    return min;
  }

  if (max != null) {
    return max;
  }

  return 0;
};

// TODO TO DEPRECATE
// Note: This function has been moved to the backend, but it is still used on the Frontend
// Its eventual removal from the frontend needs to be planned
export const getRiskProfileRuleTargets = (rule: RiskProfileRule, locale?: Locale): RiskProfileRuleTarget[] => {
  switch (rule.ruleType) {
    case RiskProfileRuleType.AssetAllocation: {
      return rule.value.map(assetRule => {
        const value = formatRiskProfileTargetMinMax(assetRule.min, assetRule.max, locale);

        return {
          key: assetRule.assetType,
          label: formatMessageWithLocale(`RiskProfile.Rules.AssetType.${assetRule.assetType}`, locale),
          value,
        };
      });
    }
    case RiskProfileRuleType.RiskLimit: {
      return [
        {
          key: 'RISK_LOWER_EQ',
          value: formatRiskProfileTargetMinMax(rule.value.min, undefined, locale),
          label: `≤ ${formatMessageWithLocale(`RiskRating.${rule.value.riskLevel}`, locale)}`,
        },
      ];
    }
    case RiskProfileRuleType.AllowedRisk: {
      return [
        {
          key: 'RISK_HIGHER',
          value: formatMaxTarget(rule.value.max, 0),
          label: `${formatMessageWithLocale(`RiskRating.${rule.value.riskLevel}`, locale)}`,
        },
      ];
    }
    case RiskProfileRuleType.FundType: {
      const value = formatRiskProfileTargetMinMax(rule.value.min, undefined, locale);
      let label: string = '';

      if (rule.labels?.description) {
        label = formatLocalizedString(rule.labels?.description, locale);
      } else {
        if (rule.value.min != null && rule.value.fundType !== RiskAssetType.MoneyMarket) {
          label += '≤ ';
        }

        label += formatMessageWithLocale(`RiskProfile.Rules.FundType.${rule.value.fundType}`, locale);
      }

      return [{ key: 'RISK_LOWER_EQ', value, label }];
    }
    default:
      return [];
  }
};

const getRiskProfileRiskLimitRuleData = (rule: RiskProfileRiskLimitRule, comparedItem?: ComparisonItem, locale?: Locale): RiskProfileRuleData[] => {
  const targets = getRiskProfileRuleTargets(rule, locale);

  const actualValues = !comparedItem ? null : splitRiskLevelValues(rule, comparedItem, RiskLevelOperator.LTE);
  const otherAlloc = !actualValues ? undefined : 100 - [actualValues.included, actualValues.excluded].reduce(sum, 0);

  return [
    {
      key: 'RISK_LOWER_EQ',
      label: targets.length > 0 ? targets[0].label : '',
      value: actualValues ? actualValues.included : undefined,
      adjustment: actualValues ? DataPoints.getTargetAdjustment(actualValues.included, rule.value.min) : undefined,
      target: {
        min: rule.value.min,
      },
    },
    {
      key: 'RISK_GREATER',
      label: '',
      value: actualValues ? actualValues.excluded : undefined,
      adjustment: actualValues ? DataPoints.getTargetAdjustment(actualValues.excluded, rule.value.min) : undefined,
      hidden: true,
      target: {
        min: 100 - rule.value.min,
      },
    },
    {
      key: 'RISK_OTHER',
      label: '',
      value: otherAlloc,
      hidden: true,
      target: {
        min: 0,
      },
    },
  ];
};

const getRiskProfileAllowedRiskRuleData = (rule: RiskProfileAllowedRiskRule, comparedItem?: ComparisonItem, locale?: Locale): RiskProfileRuleData[] => {
  const targets = getRiskProfileRuleTargets(rule, locale);

  const { operator } = rule.value;
  const actualValues = !comparedItem ? null : splitRiskLevelValues(rule, comparedItem, operator);
  const otherAlloc = !actualValues ? undefined : 100 - [actualValues.included, actualValues.excluded].reduce(sum, 0);

  let adjustment;
  if (actualValues && actualValues.included && actualValues.included > rule.value.max) {
    adjustment = rule.value.max - actualValues.included;
  }

  /*
  if (adjustment == null) {
    return [];
  }
  */

  return [
    {
      key: 'RISK_LOWER_EQ',
      label: targets.length > 0 ? targets[0].label : '',
      value: actualValues ? actualValues.included : undefined,
      adjustment,
      target: {
        max: rule.value.max,
      },
    },
    {
      key: 'RISK_GREATER',
      label: '',
      value: actualValues ? actualValues.excluded : undefined,
      adjustment: undefined,
      hidden: true,
      target: {
        max: 100 - rule.value.max,
      },
    },
    {
      key: 'RISK_OTHER',
      label: '',
      value: otherAlloc,
      hidden: true,
      target: {
        min: 0,
      },
    },
  ];
};

const getRiskProfileFundTypeRuleData = (rule: RiskProfileFundTypeRule, comparedItem?: ComparisonItem, locale?: Locale): RiskProfileRuleData[] => {
  const targets = getRiskProfileRuleTargets(rule, locale);
  const actualValues = !comparedItem ? null : splitRiskAssetTypeValues(rule, comparedItem);

  const otherAlloc = !actualValues ? undefined : 100 - [actualValues.included, actualValues.excluded].reduce(sum, 0);

  return [
    {
      key: 'RISK_LOWER_EQ',
      label: targets.length > 0 ? targets[0].label : '',
      value: actualValues ? actualValues.included : undefined,
      adjustment: actualValues ? DataPoints.getTargetAdjustment(actualValues.included, rule.value.min) : undefined,
      target: {
        min: rule.value.min,
      },
    },
    {
      key: 'RISK_GREATER',
      label: '',
      value: actualValues ? actualValues.excluded : undefined,
      adjustment: actualValues ? DataPoints.getTargetAdjustment(actualValues.excluded, rule.value.min) : undefined,
      hidden: true,
      target: {
        min: 100 - rule.value.min,
      },
    },
    {
      key: 'RISK_OTHER',
      label: '',
      value: otherAlloc,
      hidden: true,
      target: {
        min: 0,
      },
    },
  ];
};

/**
 * Sorts the asset allocation rules by their asset type.
 * @param rule RiskProfileAssetAllocationRule
 * @returns Sorted RiskProfileAssetAllocationRule
 */
export const sortAssetRule = (rule: RiskProfileAssetAllocationRule): RiskProfileAssetAllocationRule => {
  const assetRulesOrder = {
    [InvestmentAssetType.Cash]: 1,
    [InvestmentAssetType.FixedIncome]: 2,
    [InvestmentAssetType.Equity]: 3,
    [InvestmentAssetType.RealAssets]: 4,
    [InvestmentAssetType.AlternativeInvestment]: 5,
    [InvestmentAssetType.Other]: 6,
  };
  const maxOrder = Object.keys(assetRulesOrder).length + 1;
  const sortedValues = sortBy(rule.value || [], v => assetRulesOrder[v.assetType] ?? maxOrder);

  return {
    ...rule,
    value: sortedValues,
  };
};

export const getRiskProfileAssetAllocationRuleData = (
  rule: RiskProfileAssetAllocationRule,
  comparedItem?: ComparisonItem,
  locale?: Locale,
  user?: Nullable<User>,
): RiskProfileRuleData[] => {
  const sortedRule = sortAssetRule(rule);
  const assetRules = deepClone(sortedRule.value);
  let hasActualOtherRule = true;
  let hasActualAltRule = true;

  if (!assetRules.some(assetRule => assetRule.assetType === InvestmentAssetType.AlternativeInvestment) && userHasPermission(user, PERMISSIONS.PREQIN_ALT)) {
    hasActualAltRule = false;
    assetRules.push({
      assetType: InvestmentAssetType.AlternativeInvestment,
      min: 0,
      max: 0,
    });
  }

  if (!assetRules.some(assetRule => assetRule.assetType === InvestmentAssetType.Other)) {
    hasActualOtherRule = false;
    assetRules.push({
      assetType: InvestmentAssetType.Other,
      min: 0,
      max: 0,
    });
  }

  const fixedIncomeIncludesCash = !assetRules.some(assetRule => assetRule.assetType === InvestmentAssetType.Cash);
  const mixedAssetsWeight = comparedItem ? DataPoints.getMixedAssetAllocationPortion(comparedItem) : 0;
  const missingAlloc = comparedItem ? DataPoints.getItemMissingAllocationPortion(comparedItem) - mixedAssetsWeight : 0;

  const values: RiskProfileRuleData[] = assetRules.map(assetRule => {
    let value;
    let subAllocations;

    if (comparedItem) {
      const allocations = DataPoints.getAssetAllocationTotals(comparedItem);

      if (allocations) {
        value = allocations[assetRule.assetType];

        switch (assetRule.assetType) {
          case InvestmentAssetType.FixedIncome: {
            // If there's no rule specific to Cash, include it as part of Fixed income.
            if (fixedIncomeIncludesCash) {
              value += allocations[InvestmentAssetType.Cash];
            }
            break;
          }
          case InvestmentAssetType.Other: {
            // TODO: CONN-10953: Allocated weight of Mixed Assets to "Other" currently
            // But will have to account for benchmarks or anything that is not allocated elsewhere.
            value += mixedAssetsWeight;
            break;
          }
        }
      }

      // If suballocations are available, format them.
      const asset = getDetailedAssetForAssetType(comparedItem, assetRule.assetType);
      if (asset?.subAllocations?.length) {
        subAllocations = formatSuballocationsRuleData(asset.subAllocations, asset.weight);
      }
    }

    let adjustment: number | undefined;

    const mustDisplayAdjustment =
      !(assetRule.assetType === InvestmentAssetType.Other && !hasActualOtherRule) &&
      !(assetRule.assetType === InvestmentAssetType.AlternativeInvestment && !hasActualAltRule);

    if (mustDisplayAdjustment) {
      adjustment = value != null ? DataPoints.getTargetAdjustment(value, assetRule.min, assetRule.max) : undefined;
    }

    let label = formatMessageWithLocale(`RiskProfile.Rules.AssetType.${assetRule.assetType}`, locale);

    // If this is Fixed Income + Cash, use the corresponding label.
    if (assetRule.assetType === InvestmentAssetType.FixedIncome && fixedIncomeIncludesCash) {
      label = formatMessageWithLocale('RiskProfile.Rules.AssetType.CASH_FIXED_INCOME', locale);
    }

    return {
      key: assetRule.assetType,
      label,
      value,
      adjustment,
      subAllocations,
      target: {
        min: assetRule.min,
        max: assetRule.max,
      },
    };
  });

  values.push({
    key: 'RISK_OTHER',
    label: '',
    value: missingAlloc,
    hidden: true,
    target: {
      min: 0,
    },
  });

  return values;
};

export const getRiskProfileRuleData = (rule: RiskProfileRule, comparedItem?: ComparisonItem, locale?: Locale, user?: Nullable<User>): RiskProfileRuleData[] => {
  switch (rule.ruleType) {
    case RiskProfileRuleType.AssetAllocation: {
      return getRiskProfileAssetAllocationRuleData(rule, comparedItem, locale, user);
    }
    case RiskProfileRuleType.RiskLimit: {
      return getRiskProfileRiskLimitRuleData(rule, comparedItem, locale);
    }
    case RiskProfileRuleType.AllowedRisk: {
      return getRiskProfileAllowedRiskRuleData(rule, comparedItem, locale);
    }
    case RiskProfileRuleType.FundType: {
      return getRiskProfileFundTypeRuleData(rule, comparedItem, locale);
    }
    default: {
      return [];
    }
  }
};

export const castKycQuestionnaire = (currentQuestionnaire: KycQuestionnaire): Questionnaire => ({
  _id: currentQuestionnaire._id,
  type: QUESTIONNAIRE_TYPES.RISK_PROFILE,
  title: currentQuestionnaire.title,
  sections: currentQuestionnaire.sections,
  options: currentQuestionnaire.options,
  enterpriseGroups: currentQuestionnaire.enterpriseGroups,
  createdBy: currentQuestionnaire.createdBy,
});

/**
 * Override the risk colors list.
 * @param colors The list of hex-formatted colors to override.
 */
export const overrideDefaultRiskColors = (colors: { [key in RiskProfileRuleType]: Dictionary<string> }) => {
  DEFAULT_RISK_COLORS = colors;
};

export const isSummaryView = (profile: RiskProfileItem | StateOmniRiskProfileConfig) => profile.rules.length > 2;

export const formatSuballocationsRuleData = (subAllocations: LocalizedAllocationDetails[], parentWeight: number, locale: Locale = LOCALE.EN_CA) =>
  subAllocations.map(subAllocation => {
    const { name, weight } = subAllocation;

    return {
      key: name[locale]?.toLocaleLowerCase().replace(/\s/g, '-'),
      label: name[locale],
      value: (weight * parentWeight) / 100,
    };
  });

export const isUpRisk = (riskProfileUpRisk?: riskProfileUpRisk): boolean => {
  if (!riskProfileUpRisk) {
    return false;
  }

  if (typeof riskProfileUpRisk?.riskScore !== 'number' || typeof riskProfileUpRisk?.recommendedRiskScore !== 'number') {
    return false;
  }
  return riskProfileUpRisk.riskScore > riskProfileUpRisk.recommendedRiskScore;
};

export const getOriginalRiskProfile = (riskProfile: RiskProfileItem | StateOmniRiskProfileConfig, questionnaires: KycQuestionnaire[]) => {
  if (!riskProfile || !questionnaires?.length) {
    return null;
  }

  const matchingRiskProfile = questionnaires
    .flatMap(questionnaire => questionnaire.riskProfiles)
    .find(originalRiskProfile => riskProfile._id === originalRiskProfile._id);

  return matchingRiskProfile ? deepClone(matchingRiskProfile) : null;
};

/**
 * Determines if cash and fixed income should be split in the risk profile
 * @param riskProfileItem The risk profile to check
 * @returns True if there is a rule for cash, false otherwise
 */
export const shouldSplitCashAndFixedIncome = (riskProfileItem: RiskProfileItem | StateOmniRiskProfileConfig): boolean =>
  riskProfileItem.rules.some(
    rule => rule.ruleType === RiskProfileRuleType.AssetAllocation && rule.value.some(assetRule => assetRule.assetType === InvestmentAssetType.Cash),
  );
